import { Component, Suspense, lazy, useEffect, useState, type ErrorInfo, type ReactNode } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import AuthLayout from '@/layouts/AuthLayout'
import MainLayout from '@/layouts/MainLayout'

const Login = lazy(() => import('@/pages/Login'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Products = lazy(() => import('@/pages/Products'))
const Customers = lazy(() => import('@/pages/Customers'))
const Coupons = lazy(() => import('@/pages/Coupons'))
const Orders = lazy(() => import('@/pages/Orders'))
const Licenses = lazy(() => import('@/pages/Licenses'))
const LicenseDetail = lazy(() => import('@/pages/LicenseDetail'))
const TrialRequests = lazy(() => import('@/pages/TrialRequests'))
const Audit = lazy(() => import('@/pages/Audit'))
const Admins = lazy(() => import('@/pages/Admins'))

// session lives in an HttpOnly cookie; probe /me instead of a storage key
function RequireAuth() {
  const profile = useAuthStore((s) => s.profile)
  const fetchMe = useAuthStore((s) => s.fetchMe)
  const location = useLocation()
  const [state, setState] = useState<'checking' | 'ok' | 'fail'>(profile ? 'ok' : 'checking')

  useEffect(() => {
    if (profile) {
      setState('ok')
      return
    }
    setState('checking')
    let alive = true
    fetchMe()
      .then(() => alive && setState('ok'))
      .catch(() => alive && setState('fail'))
    return () => {
      alive = false
    }
  }, [profile, fetchMe])

  if (state === 'ok') return <Outlet />
  if (state === 'fail') {
    const target = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`
    return <Navigate to={target} replace />
  }
  return null
}

// 懒加载 chunk 失效（部署后文件名哈希变化、网络瞬断）会表现为
// "布局在、子视图空白"：整页刷新一次即可拿到新入口与 chunk，避免白屏卡死。
const CHUNK_RELOAD_FLAG = 'licensor-admin-chunk-reloaded'

function isChunkError(error: unknown): boolean {
  const msg = String((error as Error)?.message ?? error)
  return /dynamically imported module|Importing a module script failed|Loading chunk|Failed to fetch dynamically/i.test(msg)
}

export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    ;(window as any).__licensorError = String((error as Error)?.stack || error)
    if (isChunkError(error)) {
      if (!sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1')
        location.reload()
      }
      return
    }
    // 渲染/生命周期错误不再静默：留痕便于定位（避免只看到空白页）
    console.error('[licensor] unhandled error:', info.componentStack, error)
  }

  render() {
    if (this.state.error) {
      if (isChunkError(this.state.error)) return null
      // 可见的崩溃回退：比整页空白更可诊断
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0d0d0e', color: '#f8fafc', fontFamily: 'monospace', padding: 24 }}>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{String(this.state.error?.message || this.state.error)}</pre>
        </div>
      )
    }
    return this.props.children
  }
}

export function AppRoutes() {
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <Routes>
          <Route element={<AuthLayout />}>
            <Route path="login" element={<Login />} />
          </Route>
          <Route element={<RequireAuth />}>
            <Route element={<MainLayout />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="products" element={<Products />} />
              <Route path="customers" element={<Customers />} />
              <Route path="coupons" element={<Coupons />} />
              <Route path="orders" element={<Orders />} />
              <Route path="licenses" element={<Licenses />} />
              <Route path="licenses/:id" element={<LicenseDetail />} />
              <Route path="trial-requests" element={<TrialRequests />} />
              <Route path="audit" element={<Audit />} />
              <Route path="admins" element={<Admins />} />
            </Route>
          </Route>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* 未匹配路径兜底：直接给空白页会让人以为"登录后进不去" */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}
